import { authedJson } from './client';
import { API_ROUTES } from '../../../shared/api';
import { tzOffsetMinutes } from '../config';
import type {
  Pathway,
  LineLevel,
  StudyHome,
  RepertoireEntry,
  ReviewGrade,
  ReviewResponse,
  StudyQueue,
  StudyStats,
  UpdateSettingsRequest,
  UserSettings,
} from '../../../shared/types';

/** The auth plumbing every authenticated call needs, passed in from useAuth. */
export interface AuthArgs {
  getToken: () => string | null;
  refresh: () => Promise<boolean>;
}

/**
 * The backend needs the viewer's UTC offset to decide which calendar day a
 * review belongs to, so every study call carries it. Sending it per-request
 * rather than storing it on the profile means a user who travels gets the
 * right streak without having to update a setting.
 */
function withTz(path: string, extra?: Record<string, string | number>): string {
  const params = new URLSearchParams({ tzOffsetMinutes: String(tzOffsetMinutes()) });
  for (const [key, value] of Object.entries(extra ?? {})) {
    params.set(key, String(value));
  }
  return `${path}?${params.toString()}`;
}

export const studyApi = {
  /** Stats + repertoire in one request — what the home screen needs. */
  home: ({ getToken, refresh }: AuthArgs) =>
    authedJson<StudyHome>(withTz(API_ROUTES.studyHome), { method: 'GET' }, getToken, refresh),

  /** The suggested route through the catalog, with progress against it. */
  pathway: ({ getToken, refresh }: AuthArgs) =>
    authedJson<Pathway>(API_ROUTES.pathway, { method: 'GET' }, getToken, refresh),

  repertoire: ({ getToken, refresh }: AuthArgs) =>
    authedJson<RepertoireEntry[]>(API_ROUTES.repertoire, { method: 'GET' }, getToken, refresh),

  addToRepertoire: (openingId: string, { getToken, refresh }: AuthArgs, level?: LineLevel) =>
    authedJson<RepertoireEntry>(
      API_ROUTES.repertoire,
      { method: 'POST', body: JSON.stringify({ openingId, level }) },
      getToken,
      refresh,
    ),

  /**
   * Change what you study in an opening.
   *
   * Three ways to say what you want scheduled:
   *
   * - `levels` — the level bands you want, as a set. This is what "just the
   *   beginner lines" and "intermediate and above, but not beginner" both need,
   *   and neither is expressible with a cumulative level.
   * - `lines` — an exact selection, for per-variation checkboxes.
   * - `level` — move the cumulative baseline, clearing any per-line choices.
   *
   * The server stores the difference from the level rule rather than the set
   * itself, so variations authored later still appear.
   */
  setRepertoireSelection: (
    openingId: string,
    selection: { level?: LineLevel; lines?: string[]; levels?: LineLevel[] },
    { getToken, refresh }: AuthArgs,
  ) =>
    authedJson<RepertoireEntry>(
      API_ROUTES.repertoireEntry(openingId),
      { method: 'PUT', body: JSON.stringify(selection) },
      getToken,
      refresh,
    ),

  removeFromRepertoire: (openingId: string, { getToken, refresh }: AuthArgs) =>
    authedJson<void>(
      API_ROUTES.repertoireEntry(openingId),
      { method: 'DELETE' },
      getToken,
      refresh,
    ),

  /**
   * `openingId` narrows the session to a single opening. `mode: 'study'` asks
   * for every line in scope rather than only what is due, and ignores the daily
   * new-line budget — a study pass schedules nothing, so there is nothing to
   * ration.
   */
  queue: (
    { getToken, refresh }: AuthArgs,
    options?: {
      limit?: number;
      openingId?: string;
      /** Narrow the session to one variation — "practice this line". */
      lineId?: string;
      mode?: 'study' | 'practice';
      /**
       * `all` sets the repertoire level aside and offers every line of every
       * opening you have added. The level is there to meter what gets
       * *scheduled*; it was also deciding what you were allowed to look at,
       * which is a different question.
       */
      scope?: 'level' | 'all';
    },
  ) => {
    const extra: Record<string, string | number> = {};
    if (options?.limit != null) extra.limit = options.limit;
    if (options?.openingId) extra.openingId = options.openingId;
    if (options?.lineId) extra.lineId = options.lineId;
    if (options?.mode) extra.mode = options.mode;
    if (options?.scope) extra.scope = options.scope;
    return authedJson<StudyQueue>(
      withTz(API_ROUTES.studyQueue, extra),
      { method: 'GET' },
      getToken,
      refresh,
    );
  },

  review: (
    body: { lineId: string; grade: ReviewGrade; movesCorrect: number; movesTotal: number },
    { getToken, refresh }: AuthArgs,
  ) =>
    authedJson<ReviewResponse>(
      API_ROUTES.studyReview,
      {
        method: 'POST',
        body: JSON.stringify({ ...body, tzOffsetMinutes: tzOffsetMinutes() }),
      },
      getToken,
      refresh,
    ),

  stats: ({ getToken, refresh }: AuthArgs) =>
    authedJson<StudyStats>(withTz(API_ROUTES.studyStats), { method: 'GET' }, getToken, refresh),

  settings: ({ getToken, refresh }: AuthArgs) =>
    authedJson<UserSettings>(API_ROUTES.settings, { method: 'GET' }, getToken, refresh),

  updateSettings: (body: UpdateSettingsRequest, { getToken, refresh }: AuthArgs) =>
    authedJson<UserSettings>(
      API_ROUTES.settings,
      { method: 'PUT', body: JSON.stringify(body) },
      getToken,
      refresh,
    ),
};
